import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import SupportAdminRoute from "./components/SupportAdminRoute";
import SupportAgentRoute from "./components/SupportAgentRoute";
import SupportProtectedRoute from "./components/SupportProtectedRoute";
import SuperAdminRoute from "./components/SuperAdminRoute";
import RouteLoader from "./components/RouteLoader";
import { routeChunks } from "./routes/routeChunks";
import { LANDING_ONLY } from "./config/launchMode";

const LandingPage = lazy(routeChunks.landing);
const Register = lazy(routeChunks.register);
const Login = lazy(routeChunks.login);
const VerifyEmailNotice = lazy(routeChunks.verifyEmailNotice);
const VerifyEmail = lazy(routeChunks.verifyEmail);
const ForgotPassword = lazy(routeChunks.forgotPassword);
const ResetPassword = lazy(routeChunks.resetPassword);
const SubscribePage = lazy(routeChunks.subscribe);
const PricingPage = lazy(routeChunks.subscribe);
const BillingPage = lazy(routeChunks.billing);
const SettingsPage = lazy(routeChunks.settings);
const Dashboard = lazy(routeChunks.dashboard);
const SetupResume = lazy(routeChunks.setupResume);
const MyResumes = lazy(routeChunks.myResumes);
const RoleBasedResume = lazy(routeChunks.roleBasedResume);
const ResumePreviewPage = lazy(routeChunks.resumePreview);
const PublicResumePreview = lazy(routeChunks.publicResumePreview);
const ResumeEditPage = lazy(routeChunks.resumeEdit);
const ProfilePage = lazy(routeChunks.profile);
const InterviewPrep = lazy(routeChunks.interviewPrep);
const MockTestPage = lazy(routeChunks.mockTest);
const PlanDetailsPage = lazy(routeChunks.planDetails);
const InterviewPlans = lazy(routeChunks.interviewPlans);
const SupportPage = lazy(routeChunks.support);
const SupportLogin = lazy(routeChunks.supportLogin);
const SupportPortal = lazy(routeChunks.supportPortal);
const SupportSettings = lazy(routeChunks.supportSettings);
const SupportAdminPortal = lazy(routeChunks.supportAdmin);
const SuperAdminPortal = lazy(routeChunks.superAdmin);
const SuperAdminDepartments = lazy(routeChunks.superAdminDepartments);
const SuperAdminEmployees = lazy(routeChunks.superAdminEmployees);
const SuperAdminAssignments = lazy(routeChunks.superAdminAssignments);
const SuperAdminRoles = lazy(routeChunks.superAdminRoles);
const SuperAdminSupport = lazy(routeChunks.superAdminSupport);
const SuperAdminUsers = lazy(routeChunks.superAdminUsers);
const SuperAdminBilling = lazy(routeChunks.superAdminBilling);
const SuperAdminAiUsage = lazy(routeChunks.superAdminAiUsage);
const SuperAdminInstitutions = lazy(routeChunks.superAdminInstitutions);
const SuperAdminInfluencers = lazy(routeChunks.superAdminInfluencers);
const SuperAdminDomains = lazy(routeChunks.superAdminDomains);
const SuperAdminCouponsScope = lazy(routeChunks.superAdminCouponsScope);
const SupportLogout = lazy(routeChunks.supportLogout);
const JobApplications = lazy(routeChunks.jobApplications);
const AiResumeBuilderPage = lazy(routeChunks.aiResumeBuilder);
const JobApplicationsAssistantPage = lazy(routeChunks.jobApplicationsAssistant);
const InterviewPreparationPage = lazy(routeChunks.interviewPreparation);
const INTERVIEW_PLAN_ALLOWED_PLANS = ["basic", "pro"];

function App() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route element={<MainLayout />}>
          {LANDING_ONLY ? (
            <>
              <Route path="/" element={<LandingPage />} />
              <Route path="*" element={<LandingPage initialComingSoonOpen />} />
            </>
          ) : (
            <>
              <Route path="/" element={<LandingPage />} />
              <Route path="/features" element={<LandingPage />} />
              <Route path="/launchpad" element={<LandingPage />} />
              <Route path="/career-suite" element={<LandingPage />} />
              <Route path="/ai-resume-builder" element={<AiResumeBuilderPage />} />
              <Route
                path="/job-application-assistant"
                element={<JobApplicationsAssistantPage />}
              />
              <Route path="/interview-preparation" element={<InterviewPreparationPage />} />
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<VerifyEmailNotice />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/pricing" element={<PricingPage variant="marketing" />} />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/setup-resume"
                element={
                  <ProtectedRoute>
                    <SetupResume />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/my-resumes"
                element={
                  <ProtectedRoute>
                    <MyResumes />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/role-based-resume"
                element={
                  <ProtectedRoute>
                    <RoleBasedResume />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/resume-preview"
                element={
                  <ProtectedRoute>
                    <ResumePreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/share/:token" element={<PublicResumePreview />} />
              <Route
                path="/resume-preview/:role"
                element={
                  <ProtectedRoute>
                    <ResumePreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/resume-edit/:role"
                element={
                  <ProtectedRoute>
                    <ResumeEditPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/interview-prep"
                element={
                  <ProtectedRoute
                    allowedPlans={INTERVIEW_PLAN_ALLOWED_PLANS}
                    upgradeRedirect="/subscribe"
                  >
                    <InterviewPrep />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/prep-plan/:planId"
                element={
                  <ProtectedRoute>
                    <PlanDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/interview-plans"
                element={
                  <ProtectedRoute>
                    <InterviewPlans />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mock-test/:role"
                element={
                  <ProtectedRoute>
                    <MockTestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/job-applications"
                element={
                  <ProtectedRoute>
                    <JobApplications />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <SupportPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/u/support/login" element={<SupportLogin variant="general" />} />
              <Route
                path="/u/support/settings"
                element={
                  <SupportProtectedRoute>
                    <SupportSettings />
                  </SupportProtectedRoute>
                }
              />
              <Route
                path="/u/support/admin/login"
                element={<SupportLogin variant="supportAdmin" />}
              />
              <Route
                path="/u/support/agent/login"
                element={<SupportLogin variant="supportAgent" />}
              />
              <Route path="/u/admin/login" element={<SupportLogin variant="superadmin" />} />
              <Route
                path="/u/support/admin/logout"
                element={<SupportLogout redirectTo="/u/support/admin/login" />}
              />
              <Route
                path="/u/support/agent/logout"
                element={<SupportLogout redirectTo="/u/support/agent/login" />}
              />
              <Route
                path="/u/admin/logout"
                element={<SupportLogout redirectTo="/u/admin/login" />}
              />
              <Route
                path="/u/support/portal"
                element={
                  <SupportAgentRoute>
                    <SupportPortal />
                  </SupportAgentRoute>
                }
              />
              <Route
                path="/u/support/agent"
                element={
                  <SupportAgentRoute>
                    <SupportPortal />
                  </SupportAgentRoute>
                }
              />
              <Route
                path="/u/support/admin"
                element={
                  <SupportAdminRoute>
                    <SupportAdminPortal />
                  </SupportAdminRoute>
                }
              />
              <Route
                path="/u/support/admin/users"
                element={
                  <SupportAdminRoute>
                    <SuperAdminUsers />
                  </SupportAdminRoute>
                }
              />
              <Route
                path="/u/super/admin"
                element={
                  <SuperAdminRoute>
                    <SuperAdminPortal />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/departments"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDepartments />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/employees"
                element={
                  <SuperAdminRoute>
                    <SuperAdminEmployees />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/assignments"
                element={
                  <SuperAdminRoute>
                    <SuperAdminAssignments />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/roles"
                element={
                  <SuperAdminRoute>
                    <SuperAdminRoles />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/support"
                element={
                  <SuperAdminRoute>
                    <SuperAdminSupport />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/users"
                element={
                  <SuperAdminRoute>
                    <SuperAdminUsers />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/billing"
                element={
                  <SuperAdminRoute>
                    <SuperAdminBilling />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/institutions"
                element={
                  <SuperAdminRoute>
                    <SuperAdminInstitutions />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/influencers"
                element={
                  <SuperAdminRoute>
                    <SuperAdminInfluencers />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/domains"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDomains />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/coupons/:scope"
                element={
                  <SuperAdminRoute>
                    <SuperAdminCouponsScope />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/super/admin/ai-usage"
                element={
                  <SuperAdminRoute>
                    <SuperAdminAiUsage />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin"
                element={
                  <SuperAdminRoute>
                    <SuperAdminPortal />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/departments"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDepartments />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/employees"
                element={
                  <SuperAdminRoute>
                    <SuperAdminEmployees />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/assignments"
                element={
                  <SuperAdminRoute>
                    <SuperAdminAssignments />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/roles"
                element={
                  <SuperAdminRoute>
                    <SuperAdminRoles />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/support"
                element={
                  <SuperAdminRoute>
                    <SuperAdminSupport />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/users"
                element={
                  <SuperAdminRoute>
                    <SuperAdminUsers />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/billing"
                element={
                  <SuperAdminRoute>
                    <SuperAdminBilling />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/institutions"
                element={
                  <SuperAdminRoute>
                    <SuperAdminInstitutions />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/influencers"
                element={
                  <SuperAdminRoute>
                    <SuperAdminInfluencers />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/domains"
                element={
                  <SuperAdminRoute>
                    <SuperAdminDomains />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/coupons/:scope"
                element={
                  <SuperAdminRoute>
                    <SuperAdminCouponsScope />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/u/admin/ai-usage"
                element={
                  <SuperAdminRoute>
                    <SuperAdminAiUsage />
                  </SuperAdminRoute>
                }
              />
            </>
          )}
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
